import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useTaskingBotCategories } from '../helpers/useTaskingBotCategories';
import {
  useCreateProject,
  useTaskingBotProjects,
  useUpdateProject,
  type TaskingBotProject,
} from '../helpers/useTaskingBotProjects';
import styles from './TaskingBotProjectDialog.module.css';

const PROJECT_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#f43f5e',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#06b6d4',
  '#3b82f6',
  '#64748b',
  '#a1a1aa',
];

interface TaskingBotProjectDialogProps {
  project?: Partial<TaskingBotProject> | null;
  onClose: () => void;
}

export function TaskingBotProjectDialog({ project, onClose }: TaskingBotProjectDialogProps) {
  const isEdit = !!project?.id;
  const { data: categories = [] } = useTaskingBotCategories();
  const { data: projects = [] } = useTaskingBotProjects();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const nameRef = useRef<HTMLInputElement>(null);

  // Preserve the initial parentProjectId from props — don't let effects reset it
  const initialParentRef = useRef(project?.parentProjectId || '');

  const [name, setName] = useState(project?.name || '');
  const [description, setDescription] = useState(project?.description || '');
  const [categoryId, setCategoryId] = useState(project?.categoryId || '');
  const [parentProjectId, setParentProjectId] = useState<string>(project?.parentProjectId || '');
  const [color, setColor] = useState(project?.color || PROJECT_COLORS[0]);
  const [categoryInitialized, setCategoryInitialized] = useState(!!project?.categoryId);

  // Set default category when categories load (only if not pre-set from props)
  useEffect(() => {
    if (!categoryId && categories.length > 0) {
      setCategoryId(categories[0].id);
      setCategoryInitialized(true);
    } else if (categoryId && categories.length > 0) {
      setCategoryInitialized(true);
    }
  }, [categories, categoryId]);

  // Validate parent against category — but only after category is stable
  // and skip the initial parent that was explicitly passed from props
  useEffect(() => {
    if (!parentProjectId || projects.length === 0 || !categoryInitialized) return;
    // Don't reset the initial parent that was explicitly set from props
    if (parentProjectId === initialParentRef.current && initialParentRef.current) return;
    const parent = projects.find((p) => p.id === parentProjectId);
    if (!parent || parent.categoryId !== categoryId || parent.id === project?.id) {
      setParentProjectId('');
    }
  }, [categoryId, parentProjectId, projects, project?.id, categoryInitialized]);

  // When category changes by user action (not initialization), reset parent if mismatched
  const handleCategoryChange = (newCategoryId: string) => {
    setCategoryId(newCategoryId);
    // If parent doesn't belong to the new category, reset it
    if (parentProjectId) {
      const parent = projects.find((p) => p.id === parentProjectId);
      if (parent && parent.categoryId !== newCategoryId) {
        setParentProjectId('');
        initialParentRef.current = ''; // User changed category, clear initial ref
      }
    }
  };

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Project name is required');
      return;
    }
    // Auto-select category: from state, from parent project, or first available
    const parentCategoryId = parentProjectId ? projects.find((p) => p.id === parentProjectId)?.categoryId : undefined;
    const resolvedCategoryId = categoryId || parentCategoryId || (categories.length > 0 ? categories[0].id : '');
    if (!resolvedCategoryId) {
      toast.error('No categories available — create a category first');
      return;
    }

    console.log('[ProjectDialog] saving with parentProjectId:', parentProjectId, '→ sent as:', parentProjectId || null, 'categoryId:', resolvedCategoryId);
    try {
      if (isEdit && project?.id) {
        await updateProject.mutateAsync({
          projectId: project.id,
          name: trimmed,
          description: description.trim() || undefined,
          categoryId: resolvedCategoryId,
          parentProjectId: parentProjectId || null,
          color,
        });
        toast.success(`Updated "${trimmed}"`);
      } else {
        await createProject.mutateAsync({
          name: trimmed,
          description: description.trim() || undefined,
          categoryId: resolvedCategoryId,
          parentProjectId: parentProjectId || null,
          color,
        });
        toast.success(`Created project "${trimmed}"`);
      }
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save project');
    }
  };

  const isPending = createProject.isPending || updateProject.isPending;
  const parentOptions = projects
    .filter((p) => p.categoryId === categoryId && p.id !== project?.id)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogHeader}>
          <span className={styles.dialogTitle}>{isEdit ? 'Edit Project' : 'New Project'}</span>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className={styles.dialogBody}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Name</label>
            <input
              ref={nameRef}
              className={styles.fieldInput}
              placeholder="e.g. My SaaS App"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Description</label>
            <textarea
              className={styles.fieldTextarea}
              placeholder="What's this project about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Category</label>
            <select
              className={styles.fieldSelect}
              value={categoryId}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Parent Project (optional)</label>
            <select
              className={styles.fieldSelect}
              value={parentProjectId}
              onChange={(e) => {
                setParentProjectId(e.target.value);
                initialParentRef.current = ''; // User manually changed it
              }}
            >
              <option value="">No parent (top-level)</option>
              {parentOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Color</label>
            <div className={styles.colorRow}>
              {PROJECT_COLORS.map((c) => (
                <button
                  key={c}
                  className={`${styles.colorSwatch} ${color === c ? styles.colorSwatchActive : ''}`}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                  title={c}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={styles.dialogFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={isPending || !name.trim()}
          >
            {isPending ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
